/* eslint-disable import/no-duplicates */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Col, Row } from 'react-bootstrap';

import {
  addDays,
  format,
  startOfWeek,
  isSameWeek,
  isBefore,
  startOfTomorrow,
  parseISO,
} from 'date-fns';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useToast } from '../../hooks/toast';
import api from '../../services/api';

import Loading from '../../components/Loading';

import {
  Container,
  AgendamentoContainer,
  PData,
  PreviousNextContainer,
  DiasUnidadeContainer,
  ButtonPreviousNext,
} from './styles';
import Modal from '../../components/Modal';
import { usePerfil } from '../../hooks/perfil';
import SelectSync, { IOption } from '../../components/SelectSync';
import { completaZero } from '../../utils/funcoes';
import Horario from './Horario';
import Agendar from '../../components/Agendamento';
import { useAuth } from '../../hooks/auth';
import getResponseErrors from '../../utils/getResponseErrors';

interface IClinicaDTO {
  CliCodigo: number;
  CliNome: string;
  CliNomeAbreviado: string;
  CliCNPJ: string;
  CliTelefone: string;
  CliTelefone2: string;
  telefoneFormatado: string;
  telefone2Formatado: string;
  telefones1e2Formatado: string;
  CliEmail: string;
  CliFacebook: string;
  CliInstagram: string;
  CliSite: string;
  enderecoFormatado: string;
  CliEndereco: string;
  CliNumero: string;
  CliBairro: string;
  CliCidade: string;
  CliCidadeCodigo: string;
  CliCEP: string;
  CliComplemento: string;
  CliUF: string;
  CliLatitude: number;
  CliLongitude: number;
  CliLinkMapa: string;
}

export interface Ilinha {
  dataFormatada: string;
  ConCodigo: number;
  data: Date;
  hora: string;
  minuto: string;
  nomeAgenda?: string;
}

interface IConfiguracaoAgenda {
  ConCodigo: number;
  ConNome: string;
}

interface IDisponibilidadePorClinica {
  clinica: IClinicaDTO;
  disponibilidade?: Ilinha[][];
}

interface IPeriodo {
  inicio: Date;
  inicioFormatado: string;
  fim: Date;
  fimFormatado: string;
}

interface IDisponibilidadeRetorno {
  disponibilidadeGeral: IDisponibilidadePorClinica[];
  periodo: IPeriodo;
}

const Agendamento: React.FC = () => {
  const { addToast } = useToast();
  const { clinica } = useAuth();

  const { optionsPerfil, buscaPerfil } = usePerfil();

  const [perfilSelecionado, setPerfilSelecionado] = useState<
    number | undefined
  >(undefined);

  const [showNextPrev, setShowNextPrev] = useState({ prev: false, next: true });

  const [periodo, setPeriodo] = useState<IPeriodo>();

  const [diaSelecionado, setDiaSelecionado] = useState(() => {
    return startOfTomorrow();
  });

  const [agendaSelecionada, setAgendaSelecionada] = useState<
    Ilinha | undefined
  >(undefined);

  const optionsPerfis = useMemo(() => {
    return optionsPerfil.optionPerfis;
  }, [optionsPerfil.optionPerfis]);

  const handleNextWeek = useCallback(() => {
    setDiaSelecionado(state => {
      const proximaSemana = addDays(startOfWeek(state, { weekStartsOn: 1 }), 7);

      setShowNextPrev({ prev: true, next: true });
      return proximaSemana;
    });
  }, []);

  const handlePreviousWeek = useCallback(() => {
    setDiaSelecionado(state => {
      const inicioSemanaAtual = startOfWeek(new Date(), { weekStartsOn: 1 });
      const inicioSemanaAnterior = addDays(
        startOfWeek(state, { weekStartsOn: 1 }),
        -6,
      );

      if (
        isSameWeek(inicioSemanaAtual, inicioSemanaAnterior) ||
        isBefore(new Date(), inicioSemanaAtual)
      )
        setShowNextPrev({ prev: false, next: true });
      else setShowNextPrev({ prev: true, next: true });
      return inicioSemanaAnterior;
    });
  }, []);

  const [achouDisponibilidade, setAchouDisponibilidade] = useState(true);
  const [disponibilidade, setDisponibilidade] = useState<
    IDisponibilidadePorClinica[]
  >();
  const [showAgendando, setShowAgendando] = useState(false);

  const formRefProcedimento = useRef<FormHandles>(null);
  const handleOnChange = useCallback((dados: IOption) => {
    setPerfilSelecionado(Number(dados.value));
  }, []);

  useEffect(() => {
    async function loadPerfis(): Promise<void> {
      await buscaPerfil();
    }
    if (!optionsPerfil || Object.keys(optionsPerfil).length === 0) loadPerfis();
  }, [buscaPerfil, optionsPerfil]);

  // const dadosPerfilSelecionado = useMemo(() => {
  //   if (perfilSelecionado) {
  //     const perfil = perfis.perfis.find(p => p.PerCodigo === perfilSelecionado);
  //     if (perfil)
  //       return { PerCodigo: perfil.PerCodigo, PerNome: perfil.PerNome };
  //   }
  //   return undefined;
  // }, [perfis, perfilSelecionado]);

  const [loading, setLoading] = useState(false);

  const handleFormataPeriodo = useCallback((dados: IPeriodo) => {
    return {
      ...dados,
      inicioFormatado: format(parseISO(dados.inicio.toString()), 'dd/MM'),
      fimFormatado: format(parseISO(dados.fim.toString()), 'dd/MM'),
    };
  }, []);

  useEffect(() => {
    async function loadDisponibilidade(): Promise<void> {
      try {
        if (!perfilSelecionado) return;
        setLoading(true);

        setDisponibilidade(undefined);
        setAchouDisponibilidade(true);

        const dataFormatada = format(diaSelecionado, 'yyyy-MM-dd');

        const responseDisponibilidade = await api.get<IDisponibilidadeRetorno>(
          `/agendas/disponibilidadegeralporperfil?PerCodigo=${perfilSelecionado}&data=${dataFormatada}`,
        );

        const {
          disponibilidadeGeral,
          periodo: periodoRetorno,
        } = responseDisponibilidade.data;

        let achouDisp = false;

        const disponibilityGeral = disponibilidadeGeral.map(dis => {
          const dispoFormatada = dis.disponibilidade
            ? dis.disponibilidade.map(dia => {
                const horariosFormatados = dia.map(hor => {
                  return {
                    ...hor,
                    hora: completaZero(Number(hor.hora), '0', 2),
                  };
                });
                return horariosFormatados;
              })
            : undefined;
          if (dispoFormatada) achouDisp = true;
          return {
            clinica: {
              ...dis.clinica,
            },
            disponibilidade: dispoFormatada,
          };
        });

        const disponibilidadeClinica = disponibilityGeral.filter(
          d => d.clinica.CliCodigo === clinica.CliCodigo,
        );

        const disponibilidadeOutrasClinicas = disponibilityGeral.filter(
          d => d.clinica.CliCodigo !== clinica.CliCodigo,
        );

        const disponibilidadeOrdenada = [
          ...disponibilidadeClinica,
          ...disponibilidadeOutrasClinicas,
        ];

        setAchouDisponibilidade(achouDisp);
        setDisponibilidade(disponibilidadeOrdenada);
        setPeriodo(handleFormataPeriodo(periodoRetorno));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        const mensagem = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar disponibilidade',
          type: 'error',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }
    if (perfilSelecionado) loadDisponibilidade();
  }, [
    perfilSelecionado,
    diaSelecionado,
    handleFormataPeriodo,
    clinica.CliCodigo,
    addToast,
  ]);

  const handleSubmitProcedimento = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log('handleSubmitProcedimento');
  }, []);

  const handleFecharAgendamentoAgenda = useCallback(() => {
    setAgendaSelecionada(undefined);
    setShowAgendando(false);
  }, []);

  const handleAgendamentoSelecionado = useCallback(
    async (dados: Ilinha) => {
      try {
        if (!dados.ConCodigo) return;

        const agenda = await api.get<IConfiguracaoAgenda>(
          `/agendas/${dados.ConCodigo}/detalhe`,
        );

        setAgendaSelecionada({ ...dados, nomeAgenda: agenda.data.ConNome });
        setShowAgendando(true);
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar dados da agenda',
          type: 'error',
          description: mensagem,
        });
      }
    },
    [addToast],
  );

  return (
    <Container>
      {loading && <Loading backgroundBlack={false} />}

      {showAgendando && agendaSelecionada && perfilSelecionado && (
        <Modal
          show={showAgendando}
          titulo={`Agendamento - ${agendaSelecionada.nomeAgenda} - ${agendaSelecionada.dataFormatada} - ${agendaSelecionada.hora}:${agendaSelecionada.minuto}`}
          handleFecharModal={() => {
            setAgendaSelecionada(undefined);
            setShowAgendando(false);
          }}
        >
          <Agendar
            agendaId={agendaSelecionada.ConCodigo} // conCodigo
            perfilId={perfilSelecionado.toString()}
            data={agendaSelecionada.data}
            handleFecharAgendamento={() => handleFecharAgendamentoAgenda()}
          />
        </Modal>
      )}
      <>
        <h5 className="text-center my-4">
          Escolha um perfil para ver a disponibilidade
        </h5>

        <Row>
          <Col
            md={12}
            className="form-group pr-2 d-flex justify-content-center"
            style={{ zIndex: 2 }}
          >
            {optionsPerfis && optionsPerfis.length > 0 && (
              <Form
                ref={formRefProcedimento}
                onSubmit={handleSubmitProcedimento}
                style={{ width: '400px' }}
              >
                <SelectSync
                  name="ProPerCodigo"
                  label="Perfil"
                  placeholder="Selecione"
                  handleSelectChange={handleOnChange}
                  options={optionsPerfis}
                  size="lg"
                />
              </Form>
            )}
          </Col>
        </Row>

        <Row>
          <Col md="12">
            {!periodo && (
              <PData>
                <strong>
                  Selecione a Especialidade e Unidade para visualizar os
                  horários disponíveis
                </strong>
              </PData>
            )}
            {periodo && !loading && (
              <PData>
                HORÁRIOS DISPONÍVEIS{' '}
                {`DE ${periodo.inicioFormatado} ATÉ ${periodo.fimFormatado}`}
              </PData>
            )}
            {loading && <PData>BUSCANDO HORÁRIOS DISPONÍVEIS...</PData>}
          </Col>
        </Row>
        {/* <AgendaDoDiaContainer> */}
        <AgendamentoContainer>
          {disponibilidade &&
            perfilSelecionado &&
            !loading &&
            disponibilidade.map(
              dis =>
                dis.disponibilidade && (
                  <AgendamentoContainer
                    key={`disp-clinica-${dis.clinica.CliCodigo}`}
                    className="relative flex w-100 flex-col items-center justify-center"
                  >
                    <PreviousNextContainer>
                      <div className="d-flex items-center text-black">
                        {showNextPrev.prev && (
                          <ButtonPreviousNext
                            type="button"
                            onClick={handlePreviousWeek}
                            disabled={!showNextPrev.prev}
                          >
                            <FaChevronLeft
                              style={{ height: '36px', width: '24px' }}
                            />{' '}
                            <span>
                              Semana
                              <br />
                              Anterior
                            </span>
                          </ButtonPreviousNext>
                        )}
                      </div>
                      <div className="d-flex items-center text-black">
                        {showNextPrev.next && (
                          <ButtonPreviousNext
                            type="button"
                            onClick={handleNextWeek}
                            disabled={!showNextPrev.next}
                          >
                            <span
                              style={{
                                paddingLeft: '0',
                                paddingRight: '0.5rem',
                              }}
                            >
                              Próxima
                              <br />
                              Semana
                            </span>
                            <FaChevronRight
                              style={{ height: '36px', width: '24px' }}
                            />
                          </ButtonPreviousNext>
                        )}
                      </div>
                    </PreviousNextContainer>
                    <h4 className="text-center text-lg font-medium text-black">
                      {dis.clinica.CliBairro}
                    </h4>
                    <DiasUnidadeContainer>
                      <div className="snap mt-2 flex gap-x-3 text-white md:mt-0">
                        <Horario
                          horario={dis.disponibilidade}
                          handleAgendamentoSelecionado={(dados: Ilinha) => {
                            handleAgendamentoSelecionado(dados);
                          }}
                        />
                      </div>
                    </DiasUnidadeContainer>
                  </AgendamentoContainer>
                ),
            )}
          {loading && <Loading />}

          {(!disponibilidade ||
            disponibilidade.length === 0 ||
            !achouDisponibilidade) &&
            !loading &&
            periodo && (
              <div style={{ height: '150px' }}>
                <PreviousNextContainer>
                  <div className="d-flex items-center text-black">
                    <p className="my-4 font-medium">
                      Não encontramos nenhum horário disponível para essa
                      semana.
                    </p>
                    {showNextPrev.prev && (
                      <ButtonPreviousNext
                        type="button"
                        onClick={handlePreviousWeek}
                        disabled={!showNextPrev.prev}
                      >
                        <FaChevronLeft
                          style={{ height: '36px', width: '24px' }}
                        />{' '}
                        <span>
                          Semana
                          <br />
                          Anterior
                        </span>
                      </ButtonPreviousNext>
                    )}
                  </div>
                  <div className="d-flex items-center text-black">
                    {showNextPrev.next && (
                      <ButtonPreviousNext
                        type="button"
                        onClick={handleNextWeek}
                        disabled={!showNextPrev.next}
                      >
                        <span
                          style={{
                            paddingLeft: '0',
                            paddingRight: '0.5rem',
                          }}
                        >
                          Próxima
                          <br />
                          Semana
                        </span>
                        <FaChevronRight
                          style={{ height: '36px', width: '24px' }}
                        />
                      </ButtonPreviousNext>
                    )}
                  </div>
                </PreviousNextContainer>
              </div>
            )}
        </AgendamentoContainer>
        {/* </AgendaDoDiaContainer> */}
      </>
    </Container>
  );
};

export default Agendamento;
